/**
 * SPA hooks for M4 Amazon tracking — module-gated on 'amazon'. State is a
 * read-only snapshot the API keeps from SP-API pulls; sync is an explicit
 * user click (hard rule: no automated Amazon writes — this only reads).
 *
 * Also home to the pure helpers behind AmazonPage's density-table toolbar
 * (search, state filter + counts, column sort) so they're unit-testable
 * without rendering — see amazon.test.ts.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AmazonStateResponse,
  AmazonSyncResponse,
  AmazonUnitFlowResponse,
  SetAmazonListingPrepWrapResponse,
  type AmazonInventoryRow,
  type AmazonUnitFlowCoverage,
  type AmazonUnitFlowRow,
  type SetAmazonListingPrepWrapRequest,
} from '@sourcito/schemas';
import { apiFetch } from './api';
import type { MathCardData, MathCardLine } from './order-planning';
import { money } from './order-planning';
import { shortDateLabel } from './shipments';

export const UNIT_FLOW_HEADER_CLASS =
  'hidden md:grid md:grid-cols-[minmax(0,1.5fr)_repeat(3,minmax(0,1fr))] gap-3 px-4 py-2 text-[10px] uppercase tracking-[0.14em] text-fg-tertiary border-b border-border-subtle';
export const UNIT_FLOW_ROW_CLASS =
  'grid gap-3 px-4 py-3 md:grid-cols-[minmax(0,1.5fr)_repeat(3,minmax(0,1fr))] md:items-center hover:bg-surface-elev/50 transition-colors duration-fast';

export function useAmazonState(enabled = true) {
  return useQuery({
    queryKey: ['amazon-state'] as const,
    enabled,
    queryFn: () => apiFetch('/api/amazon', { responseSchema: AmazonStateResponse }),
  });
}

export function useAmazonUnitFlow(enabled = true) {
  return useQuery({
    queryKey: ['amazon-unit-flow'] as const,
    enabled,
    queryFn: () => apiFetch('/api/amazon/unit-flow', { responseSchema: AmazonUnitFlowResponse }),
  });
}

/** PATCH /api/amazon/listings/:sellerSku — the COGS column's "Glass / wrap"
 *  toggle (Faraz 2026-09-07). Optimistic (2026-09-10 fix, Faraz: "It goes
 *  off and then comes back after a pause. User does not know what is
 *  happening" — the checkbox is controlled by the ['amazon-state'] query
 *  and was only invalidated on success, so it visibly snapped back to the
 *  old value for the length of the refetch): onMutate cancels the
 *  in-flight ['amazon-state'] query, snapshots it, and patches the
 *  matching row's prepRequiresWrap (+ prepWrapSource: 'manual', matching
 *  what the API actually sets once a human flips this) into the cache so
 *  the checkbox updates immediately; onError restores the snapshot;
 *  onSettled invalidates so the next real read is authoritative either
 *  way. */
export function useSetAmazonListingPrepWrap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sellerSku,
      ...input
    }: { sellerSku: string } & SetAmazonListingPrepWrapRequest) =>
      apiFetch(`/api/amazon/listings/${encodeURIComponent(sellerSku)}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
        responseSchema: SetAmazonListingPrepWrapResponse,
      }),
    onMutate: async ({ sellerSku, prepRequiresWrap }) => {
      await qc.cancelQueries({ queryKey: ['amazon-state'] });
      const previous = qc.getQueryData<AmazonStateResponse>(['amazon-state']);
      qc.setQueryData<AmazonStateResponse>(['amazon-state'], (old) => {
        if (!old) return old;
        return {
          ...old,
          inventory: old.inventory.map((row) =>
            row.sellerSku === sellerSku
              ? { ...row, prepRequiresWrap, prepWrapSource: 'manual' as const }
              : row
          ),
        };
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) qc.setQueryData(['amazon-state'], context.previous);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['amazon-state'] });
    },
  });
}

export function useAmazonSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch('/api/amazon/sync', { method: 'POST', responseSchema: AmazonSyncResponse }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['amazon-state'] });
      qc.invalidateQueries({ queryKey: ['amazon-unit-flow'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      // A sync applies FBA check-ins against shipment lines, so shipment
      // state is stale too once it completes.
      qc.invalidateQueries({ queryKey: ['fba-shipments'] });
    },
  });
}

// ─────────────────────────────────────────────────────────
// Pure inventory-row helpers
// ─────────────────────────────────────────────────────────

/** Total units inbound to FBA across all three inbound states. */
export function inboundTotal(
  row: Pick<AmazonInventoryRow, 'inboundWorking' | 'inboundShipped' | 'inboundReceiving'>
): number {
  return row.inboundWorking + row.inboundShipped + row.inboundReceiving;
}

// ─────────────────────────────────────────────────────────
// Toolbar: search (client-side, SKU/ASIN/name)
// ─────────────────────────────────────────────────────────

/** Case-insensitive substring match across the three fields the search box
 *  covers. An empty/whitespace query matches everything. */
export function matchesAmazonSearch(
  row: Pick<AmazonInventoryRow, 'sellerSku' | 'asin' | 'productName'>,
  query: string
): boolean {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return true;
  return (
    row.sellerSku.toLowerCase().includes(trimmed) ||
    (row.asin?.toLowerCase().includes(trimmed) ?? false) ||
    (row.productName?.toLowerCase().includes(trimmed) ?? false)
  );
}

export function searchAmazonInventory<T extends Parameters<typeof matchesAmazonSearch>[0]>(
  rows: T[],
  query: string
): T[] {
  return rows.filter((r) => matchesAmazonSearch(r, query));
}

// ─────────────────────────────────────────────────────────
// Toolbar: state filter pills — All / Selling / Not selling. "Selling" is a
// Phase A stand-in ("sellable stock until sales data lands (Phase B)") —
// fulfillable > 0, not an actual sales signal.
// ─────────────────────────────────────────────────────────

export type AmazonStateFilterKey = 'all' | 'selling' | 'not_selling';

export const DEFAULT_AMAZON_STATE_FILTER: AmazonStateFilterKey = 'all';

export const AMAZON_STATE_FILTER_PILLS: { key: AmazonStateFilterKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'selling', label: 'Selling' },
  { key: 'not_selling', label: 'Not selling' },
];

/** Truly out of stock: zero units anywhere at Amazon — nothing fulfillable,
 *  nothing reserved, nothing inbound. Products with stock on the way stay
 *  visible (they're returning to sellable, not dead). Hidden by default on
 *  the Amazon page (Faraz 2026-09-01); a toolbar toggle shows them. */
export function isOutOfStock(
  row: Pick<
    AmazonInventoryRow,
    'fulfillable' | 'reserved' | 'inboundWorking' | 'inboundShipped' | 'inboundReceiving'
  >
): boolean {
  return (
    row.fulfillable === 0 &&
    row.reserved === 0 &&
    row.inboundWorking === 0 &&
    row.inboundShipped === 0 &&
    row.inboundReceiving === 0
  );
}

export function matchesAmazonStateFilter(
  row: Pick<AmazonInventoryRow, 'fulfillable'>,
  filter: AmazonStateFilterKey
): boolean {
  if (filter === 'all') return true;
  if (filter === 'selling') return row.fulfillable > 0;
  return row.fulfillable === 0;
}

export function filterAmazonInventoryByState<
  T extends Parameters<typeof matchesAmazonStateFilter>[0],
>(rows: T[], filter: AmazonStateFilterKey): T[] {
  return rows.filter((r) => matchesAmazonStateFilter(r, filter));
}

/** Pure and total: every pill key present even for an empty list, so
 *  callers can render "Label · 0" without a fallback branch. */
export function amazonStateFilterCounts(
  rows: Pick<AmazonInventoryRow, 'fulfillable'>[]
): Record<AmazonStateFilterKey, number> {
  const counts: Record<AmazonStateFilterKey, number> = {
    all: rows.length,
    selling: 0,
    not_selling: 0,
  };
  for (const r of rows) {
    if (r.fulfillable > 0) counts.selling++;
    else counts.not_selling++;
  }
  return counts;
}

// ─────────────────────────────────────────────────────────
// PERFORMANCE column (Make Today real phase 2, Instrument 3, 2026-09-01) —
// unitsSold7d/30d come from the API's shared velocity aggregation
// (lib/amazon/sales-velocity.ts server-side). Null means velocity is
// unknown (no/insufficient sales in the last 30 days), never a bare zero —
// same convention as price-calibration's velocityKnown; the em-dash says
// so instead of implying zero sales.
// ─────────────────────────────────────────────────────────

export function performancePrimaryLabel(unitsSold30d: number | null): string {
  if (unitsSold30d == null) return '—';
  return `${unitsSold30d.toLocaleString('en-US')} sold / 30d`;
}

export function performanceSecondaryLabel(unitsSold7d: number | null): string {
  if (unitsSold7d == null) return '7d: —';
  return `7d: ${unitsSold7d.toLocaleString('en-US')}`;
}

// ─────────────────────────────────────────────────────────
// PRICE & FEES column — buy box wording matches PriceCalibrationRow.tsx's
// established copy convention (words, not a bare dash, for a null buy
// box — Faraz UAT finding, 2026-08-31: a dash read as "price missing").
// ─────────────────────────────────────────────────────────

export const NO_BUYBOX_LABEL = 'No featured offer (buy box) on this listing right now';

export function buyboxDisplayLabel(
  row: Pick<AmazonInventoryRow, 'buyboxPriceCents' | 'buyboxIsYours'>
): string {
  if (row.buyboxPriceCents == null) return NO_BUYBOX_LABEL;
  const priceLabel = money(row.buyboxPriceCents);
  return row.buyboxIsYours === true ? `${priceLabel} (yours)` : priceLabel;
}

/** "fees $X.XX" (or "fees —" when the fees leg hasn't priced this SKU yet)
 *  — money() already renders the dash, never a fake $0.00. */
export function feesDisplayLabel(feesAtPriceCents: number | null): string {
  return `fees ${money(feesAtPriceCents)}`;
}

/** Checked-at subtext/tooltip for the fees line — same shortDateLabel
 *  convention as priceFreshnessLabel (lib/price-calibration.ts). */
export function feesCheckedAtLabel(feesCheckedAt: string | null): string {
  return feesCheckedAt ? shortDateLabel(feesCheckedAt) : '—';
}

// ─────────────────────────────────────────────────────────
// COGS column (Faraz 2026-09-07: I need a column in OPS-Amazon for COGS;
// follow-up: prep cost too, $1.00 or $1.50 for glass) — cogsUnitCents/
// cogsBreakdown/cogsGrade come from the API's shared reader
// (lib/amazon/inventory-cogs.ts server-side, itself the one lib/finance/
// landed-cost.ts kernel Price Calibration and the Dashboard also use).
// "—" when unknown, same honest-null convention as the PERFORMANCE column.
// ─────────────────────────────────────────────────────────

export function cogsPrimaryLabel(unitCents: number | null): string {
  return money(unitCents);
}

/** "at cost: $X" where X is the full on-hand position (fulfillable +
 *  reserved) valued at the per-unit COGS — the dollar exposure this row's
 *  physical stock represents, not just the per-unit price. */
export function cogsAtCostLabel(
  row: Pick<AmazonInventoryRow, 'cogsUnitCents' | 'fulfillable' | 'reserved'>
): string {
  if (row.cogsUnitCents == null) return 'at cost: —';
  return `at cost: ${money((row.fulfillable + row.reserved) * row.cogsUnitCents)}`;
}

/** Tooltip/title breakdown for the primary COGS figure — Faraz 2026-09-07
 *  (one-unit-cost design): "goods $4.10 + surcharges $0.22 + prep $0.53
 *  actual" (settlement — every unit covered by a warehouse cost layer),
 *  "goods $4.10 + surcharges $0.22 + prep $1.00 accrued (no warehouse
 *  invoice for this month yet)" (accrued — some units still on the flat
 *  buying-settings rate), or "estimate from latest invoice" (no received
 *  lots at all). Undefined (no title) when COGS is unknown — nothing to
 *  break down.
 *
 *  Faraz 2026-09-08 glass-hint design: "cogsBreakdownTitle names the prep
 *  source" — only the auto-wrapped (catalog) case is called out; a manual
 *  flag or the plain default are already communicated by the toggle
 *  itself, so the title is unchanged for those. `prepWrapSource` is
 *  optional here (not part of the Pick) so existing callers that only know
 *  cogsBreakdown/cogsGrade keep compiling. */
export function cogsBreakdownTitle(
  row: Pick<AmazonInventoryRow, 'cogsBreakdown' | 'cogsGrade'> & {
    prepWrapSource?: AmazonInventoryRow['prepWrapSource'];
  }
): string | undefined {
  if (row.cogsGrade === 'estimate') return 'estimate from latest invoice';
  const b = row.cogsBreakdown;
  if (
    row.cogsGrade == null ||
    b == null ||
    b.baseCents == null ||
    b.poChargeCents == null ||
    b.warehouseCents == null ||
    b.prepAccrualCents == null
  ) {
    return undefined;
  }
  const prepCents = b.warehouseCents + b.prepAccrualCents;
  const prepLabel =
    row.cogsGrade === 'accrued'
      ? `${money(prepCents)} accrued (no warehouse invoice for this month yet)`
      : `${money(prepCents)} actual`;
  const wrapSourceSuffix = row.prepWrapSource === 'catalog' ? ' — wrap: catalog glass hint' : '';
  return `goods ${money(b.baseCents)} + surcharges ${money(b.poChargeCents)} + prep ${prepLabel}${wrapSourceSuffix}`;
}

// ─────────────────────────────────────────────────────────
// Sortable columns — name, fulfillable, reserved, updatedAt, cogs.
// ─────────────────────────────────────────────────────────

export type AmazonSortKey = 'name' | 'fulfillable' | 'reserved' | 'updatedAt' | 'cogs';
export type AmazonSortDirection = 'asc' | 'desc';

type SortableRow = Pick<
  AmazonInventoryRow,
  'sellerSku' | 'productName' | 'fulfillable' | 'reserved' | 'updatedAt' | 'cogsUnitCents'
>;

/** `productName` is nullable — falls back to the SKU so a row without a
 *  synced name still sorts predictably instead of clumping at one end. */
function amazonSortName(row: Pick<SortableRow, 'sellerSku' | 'productName'>): string {
  return row.productName ?? row.sellerSku;
}

export function compareAmazonInventory(
  a: SortableRow,
  b: SortableRow,
  key: AmazonSortKey,
  direction: AmazonSortDirection
): number {
  // COGS: null (unknown) sorts last regardless of direction — flipping the
  // whole comparator for desc would otherwise put unknowns first.
  if (key === 'cogs') {
    if (a.cogsUnitCents == null && b.cogsUnitCents == null) return 0;
    if (a.cogsUnitCents == null) return 1;
    if (b.cogsUnitCents == null) return -1;
    const cmp = a.cogsUnitCents - b.cogsUnitCents;
    return direction === 'asc' ? cmp : -cmp;
  }
  let cmp: number;
  switch (key) {
    case 'name':
      cmp = amazonSortName(a).localeCompare(amazonSortName(b));
      break;
    case 'fulfillable':
      cmp = a.fulfillable - b.fulfillable;
      break;
    case 'reserved':
      cmp = a.reserved - b.reserved;
      break;
    case 'updatedAt':
      cmp = Date.parse(a.updatedAt) - Date.parse(b.updatedAt);
      break;
  }
  return direction === 'asc' ? cmp : -cmp;
}

export function sortAmazonInventory<T extends SortableRow>(
  rows: T[],
  key: AmazonSortKey,
  direction: AmazonSortDirection
): T[] {
  return [...rows].sort((a, b) => compareAmazonInventory(a, b, key, direction));
}

// ─────────────────────────────────────────────────────────
// Unit-flow display adapter — keeps the server's ranked order and only adds
// labels/formatting for the Amazon page. Ledger arithmetic remains server-side.
// ─────────────────────────────────────────────────────────

export interface AmazonUnitFlowDisplayRow {
  sellerSku: string;
  asin: string | null;
  productName: string | null;
  mappingLabel: string;
  warehouseExpectedOnHand: string;
  amazonResidual: string;
  weightedLotCost: string;
  dollarExposure: string;
  cardData: MathCardData;
  /** True when either leg has a non-zero residual — units left (or
   *  appeared) with no paper trail. Attention rows sort first and get a
   *  warning tint; balanced rows collapse behind a summary line. */
  needsAttention: boolean;
}

/** Attention rows first (each leg's residual unexplained), balanced rows
 *  after; original relative order (API's exposure ranking) preserved
 *  within each group. */
export function sortUnitFlowDisplayRows(
  rows: AmazonUnitFlowDisplayRow[]
): AmazonUnitFlowDisplayRow[] {
  return [...rows.filter((r) => r.needsAttention), ...rows.filter((r) => !r.needsAttention)];
}

export type UnitFlowCoverageDisplay =
  | {
      kind: 'complete';
      message: 'Source coverage complete.';
      toneClass: 'text-fg-secondary';
      subtitle: 'Warehouse and Amazon inventory variance, ranked by estimated exposure.';
    }
  | {
      kind: 'incomplete';
      message: string;
      toneClass: 'text-warning';
      subtitle: 'Unit rows remain visible while source backfill completes; dollar exposure is withheld.';
    }
  | {
      kind: 'unavailable';
      message: 'Source coverage unavailable — refresh to verify.';
      toneClass: 'text-warning';
      subtitle: 'Unit rows remain visible while source coverage is unavailable; dollar exposure is withheld.';
    };

const COVERAGE_LEG_LABELS: Record<keyof AmazonUnitFlowCoverage['legs'], string> = {
  sales: 'Sales',
  adjustments: 'Adjustments',
  removals: 'Removals',
  reimbursements: 'Reimbursements',
};

function coverageDate(value: string | null): string | null {
  return value ? value.slice(0, 10) : null;
}

export function unitFlowCoverageDisplay(
  coverage: AmazonUnitFlowCoverage | null | undefined
): UnitFlowCoverageDisplay {
  if (!coverage)
    return {
      kind: 'unavailable',
      message: 'Source coverage unavailable — refresh to verify.',
      toneClass: 'text-warning',
      subtitle:
        'Unit rows remain visible while source coverage is unavailable; dollar exposure is withheld.',
    };
  if (coverage.complete) {
    return {
      kind: 'complete',
      message: 'Source coverage complete.',
      toneClass: 'text-fg-secondary',
      subtitle: 'Warehouse and Amazon inventory variance, ranked by estimated exposure.',
    };
  }

  const incomplete = (
    Object.keys(COVERAGE_LEG_LABELS) as Array<keyof AmazonUnitFlowCoverage['legs']>
  )
    .map((key) => {
      const leg = coverage.legs[key];
      if (leg.status === 'caught_up') return null;
      const label = COVERAGE_LEG_LABELS[key];
      const status = leg.status === 'unknown' ? 'coverage unknown' : leg.status;
      const next = coverageDate(leg.nextWindowStart);
      const last = coverageDate(leg.lastCompletedAt);
      const progress = next ? `next ${next}` : last ? `through ${last}` : 'no progress';
      return `${label} ${status} (${progress})`;
    })
    .filter((detail): detail is string => detail !== null);
  return {
    kind: 'incomplete',
    message: `Source coverage incomplete: ${incomplete.length > 0 ? incomplete.join('; ') : 'refresh pending'}.`,
    toneClass: 'text-warning',
    subtitle:
      'Unit rows remain visible while source backfill completes; dollar exposure is withheld.',
  };
}

function unitAmount(units: number): string {
  return `${units < 0 ? '−' : ''}${Math.abs(units)} units`;
}

function residualAmount(units: number): string {
  return units > 0 ? `+${units} units` : unitAmount(units);
}

function sourceLine(
  label: string,
  sign: MathCardLine['sign'],
  amount: string,
  annotation: string
): MathCardLine {
  return { label, sign, amount, annotation };
}

function signedMoney(cents: number): string {
  if (cents > 0) return `+${money(cents)}`;
  if (cents < 0) return `−${money(Math.abs(cents))}`;
  return money(0);
}

export function unitFlowRowToDisplay(
  row: AmazonUnitFlowRow,
  coverage: AmazonUnitFlowCoverage | null | undefined = null
): AmazonUnitFlowDisplayRow {
  const mappingLabel =
    row.mappingState === 'mapped'
      ? `Mapped${row.vendorItemNo ? ` to ${row.vendorItemNo}` : ''}`
      : row.mappingState === 'shared'
        ? 'Shared mapping — warehouse unavailable'
        : 'Unmapped — warehouse unavailable';
  const warehouseUnavailableLabel =
    row.mappingState === 'shared'
      ? 'unavailable — shared mapping'
      : row.mappingState === 'unmapped'
        ? 'unavailable — unmapped mapping'
        : 'unavailable — no warehouse lot data';
  const warehouseLines: MathCardLine[] = row.warehouse
    ? [
        sourceLine(
          'warehouse received',
          '',
          unitAmount(row.warehouse.lotReceived),
          'source: inventory lots'
        ),
        sourceLine(
          'warehouse on hand',
          '−',
          unitAmount(row.warehouse.warehouseOnHand),
          'source: inventory lots'
        ),
        sourceLine(
          'allocated to shipments',
          '−',
          unitAmount(row.warehouse.allocatedToShipments),
          'source: shipment allocations'
        ),
        sourceLine(
          'warehouse residual',
          '=',
          residualAmount(row.warehouse.residualUnits),
          coverage?.complete === false
            ? 'unavailable — source coverage/backfill unavailable · vendor units'
            : coverage == null
              ? 'unavailable — source coverage unavailable · vendor units'
              : row.warehouseResidualCents == null
                ? 'uncosted residual · vendor units'
                : 'derived from warehouse sources · vendor units'
        ),
      ]
    : [sourceLine('warehouse residual', '=', 'unavailable', mappingLabel)];
  const amazonLines: MathCardLine[] = [
    sourceLine('FBA received', '', unitAmount(row.amazon.fbaReceived), 'source: shipment lines'),
    sourceLine('sold', '−', unitAmount(row.amazon.soldUnits), 'source: Amazon sales'),
    sourceLine('FBA on hand', '−', unitAmount(row.amazon.fbaOnHand), 'source: Amazon inventory'),
    sourceLine(
      'adjustments',
      row.amazon.adjustments < 0 ? '−' : '+',
      unitAmount(Math.abs(row.amazon.adjustments)),
      'source: Amazon adjustments'
    ),
    sourceLine('removed', '−', unitAmount(row.amazon.removedUnits), 'source: Amazon removals'),
    sourceLine(
      'Amazon inventory variance',
      '=',
      residualAmount(row.amazon.residualUnits),
      row.amazonResidualCents == null
        ? 'unavailable · listing units'
        : 'derived from Amazon sources · listing units'
    ),
    sourceLine(
      'financial recovery',
      '',
      signedMoney(row.amazon.reimbursedCents ?? 0),
      `reported ${row.amazon.reimbursedUnits} ${row.amazon.reimbursedUnits === 1 ? 'unit' : 'units'} · excluded from physical inventory · cash-vs-inventory quantity is not classified`
    ),
  ];
  const weightedLotCost =
    row.cost.weightedVendorUnitCents == null
      ? 'unavailable — no contributing lot cost'
      : `${money(row.cost.weightedVendorUnitCents)} / vendor unit estimate`;
  const dollarExposure =
    coverage?.complete === false
      ? 'unavailable — sources backfilling'
      : coverage == null
        ? 'unavailable — source coverage unavailable'
        : row.residualCents == null
          ? 'unavailable — uncosted residual'
          : `${money(row.residualCents)} estimate`;
  const cardData: MathCardData = {
    lines: [
      ...warehouseLines,
      ...amazonLines,
      sourceLine(
        'weighted lot cost',
        '',
        row.cost.weightedVendorUnitCents == null
          ? 'unavailable'
          : money(row.cost.weightedVendorUnitCents),
        row.cost.weightedVendorUnitCents == null
          ? 'no contributing lot cost'
          : `estimate · weighted from ${row.cost.contributingLotUnits} lot units`
      ),
      ...(row.unitsPerListing > 1
        ? [
            sourceLine(
              'listing-unit cost',
              '',
              row.cost.listingUnitCents == null ? 'unavailable' : money(row.cost.listingUnitCents),
              row.cost.listingUnitCents == null
                ? 'unavailable — no listing-unit cost'
                : row.cost.weightedVendorUnitCents == null
                  ? 'derived from unavailable weighted vendor cost'
                  : `estimate · ${row.unitsPerListing} vendor units × weighted vendor cost (${money(row.cost.weightedVendorUnitCents)} / vendor unit)`
            ),
          ]
        : []),
      sourceLine(
        'dollar exposure',
        '',
        dollarExposure,
        dollarExposure.startsWith('unavailable')
          ? dollarExposure.replace('unavailable — ', '')
          : 'estimate · absolute residual value'
      ),
    ],
    thresholdsLine: null,
    trendWarningLine: null,
    buyBoxLine: null,
    authorizationLine: null,
    verdictLine: `Mapping: ${mappingLabel}`,
  };

  return {
    sellerSku: row.sellerSku,
    asin: row.asin,
    productName: row.productName,
    mappingLabel,
    warehouseExpectedOnHand: row.warehouse
      ? unitAmount(row.warehouse.warehouseOnHand)
      : warehouseUnavailableLabel,
    amazonResidual: residualAmount(row.amazon.residualUnits),
    weightedLotCost,
    dollarExposure,
    cardData,
    needsAttention: row.amazon.residualUnits !== 0 || (row.warehouse?.residualUnits ?? 0) !== 0,
  };
}

export function unitFlowRowsToDisplay(
  rows: AmazonUnitFlowRow[],
  coverage: AmazonUnitFlowCoverage | null | undefined = null
): AmazonUnitFlowDisplayRow[] {
  return rows.map((row) => unitFlowRowToDisplay(row, coverage));
}
